"use client"

import { useState, useEffect, useRef } from "react"
import "./NearProcess.css"

const NearProcess = () => {
  const [activeTab, setActiveTab] = useState("profiling")
  const headerRef = useRef(null)
  const textRef = useRef(null)
  const rightTextRef = useRef(null)
  const imageRef = useRef(null)
  const tabsRef = useRef(null)

  const tabContent = {
    profiling: {
      title: "PROFILING",
      subtitle: "1.TEAM REQUIREMENTS",
      steps: ["2. ENABLEMENT", "3. PROFILE SELECTION"],
      description: [
        "Team Requirements: Our team initiates the Managed Teams Process with a comprehensive Profiling stage. During this phase, we collaborate closely with the client to capture project goals and position requirements. This includes evaluating experience, tech stack preferences, and required seniority levels.",
        "Enablement: Drawing from our extensive experience in hiring tech professionals, we offer valuable insights to enhance team configurations. Whether the client has a predefined team structure or seeks assistance in designing one, we ensure a well-balanced team tailored to meet project objectives.",
        "Profile Selection: This step finally involves identifying the best-suited candidates based on client requirements, providing detailed curriculums, and facilitating interviews or technical challenges as needed.",
      ],
    },
    enable: {
      title: "ENABLEMENT",
      steps: ["4. ADMINISTRATIVE SETUP", "5. ONBOARDING"],
      items: [
       "Administrative Setup: The Enablement stage focuses on setting the foundation for a successful project launch. Beginning with Administrative Setup, our team manages all necessary tasks to expedite the project, handling paperwork, contract signing, and acquiring or configuring hardware and software per client specifications.",
       "Onboarding: The Onboarding step ensures internal team immersion in the industry, client specifics, and project details, facilitating a quicker ramp-up. Additionally, the team actively participates in any client onboarding processes as required."
      ],
    },
    development: {
      title: "DEVELOPMENT",
       steps: ["6. Career Development & Training", "7. Account Business Reviews"],
      items: [
        "Career Development & Training: Development is a pivotal stage in our Managed Teams Process, focusing on the continuous improvement and growth of our team members. Career Development & Training ensures each employee follows a tailored plan to enhance their skills. Supported by People Experience Partners and internal line managers, our team overcomes project hurdles effectively.",
        "Account Business Reviews: Account Business Reviews, facilitated by designated Account Managers, offer clients regular check-ins to monitor project progress, align with business goals, and identify opportunities for improvement."
      ],
    },
    phase: {
      title: "PHASEDOWN",
      steps: ["8. Administrative Closure", "9. Account Debrief"],
      items: [
        "Administrative Closure: In the Phasedown stage, our team ensures a smooth conclusion to the Managed Teams Process. Administrative Closure involves handling all steps to close the process seamlessly, including returning hardware or software based on client requirements. A comprehensive handover of pending tasks and documentation is delivered as per the client's request.",
        "Account Debrief: The final step, Account Debrief, involves an interview between the Account Manager and the client to ensure a thorough review of the project, leaving no loose ends."
      ],
    },
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
          }
        })
      },
      { threshold: 0.1 },
    )

    const elements = [headerRef.current, textRef.current, rightTextRef.current, imageRef.current, tabsRef.current]
    elements.forEach((el) => {
      if (el) observer.observe(el)
    })

    return () => {
      elements.forEach((el) => {
        if (el) observer.unobserve(el)
      })
    }
  }, [])

  return (
    <div className="scoping-container" id="process">
      <div className="container">
      <div className="scoping-wrapper">
        {/* Header */}

        {/* Main Content */}
        {/* Main Content - 2x2 Grid */}
        <div className="main-content-grid">
          {/* Top Row */}
          <div className="header-title-section" ref={headerRef}>
            <h2 className="header-title7166">
              OUR
              <br />
              <span className="highlight">NEARSHORE TEAMS</span>
              <br />
              <span className="highlight">PROCESS</span>
            </h2>
          </div>
          <div className="top-right-text" ref={rightTextRef}>
            <p className="right-side-text">
              From profiling to enablement, we get a team ready in up to 2 weeks, ensuring a 90% match rate on technical skills and cultural alignment. At our core, we prioritize understanding your vision and crafting a team that aligns seamlessly with your goals.
            </p>
          </div>

          {/* Bottom Row */}
          <div className="text-section" ref={textRef}>
            <p className="main-description">
              Through a collaborative and consultative approach, we provide the expertise and resources necessary to
              ensure your project's success. We provide experienced project managers, enabling you to hit the ground
              running with a qualified and experienced team. The project is delivered through our nearshore development
              process and support delivered to ensure your project and team. Our experienced talent pool of
              professionals with the knowledge and expertise to deliver.
            </p>
          </div>
          <div className="image-section" ref={imageRef}>
            <img
              src="/process-scoping.webp?height=300&width=400"
              alt="Nearshore Process Illustration"
              className="process-image"
            />
          </div>
        </div>

        {/* Tabs Section */}
        <div className="tabs-container" ref={tabsRef}>
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === "profiling" ? "active" : ""}`}
              onClick={() => setActiveTab("profiling")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              PROFILING
            </button>
            <button
              className={`tab-button ${activeTab === "enable" ? "active" : ""}`}
              onClick={() => setActiveTab("enable")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              ENABLEMENT
            </button>
            <button
              className={`tab-button ${activeTab === "development" ? "active" : ""}`}
              onClick={() => setActiveTab("development")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </div>
              DEVELOPMENT
            </button>
            <button
              className={`tab-button ${activeTab === "phase" ? "active" : ""}`}
              onClick={() => setActiveTab("phase")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <rect x="7" y="7" width="3" height="9" />
                  <rect x="14" y="7" width="3" height="5" />
                </svg>
              </div>
              PHASEDOWN
            </button>
          </div>

          <div className="tab-content">
            <div className={`tab-panel ${activeTab === "profiling" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.profiling.title}</h3>
              <h4 className="tab-subtitle1123">{tabContent.profiling.subtitle}</h4>
              <h4 className="tab-subtitle1123">{tabContent.profiling.steps[0]}</h4>
              <h4 className="tab-subtitle1123">{tabContent.profiling.steps[1]}</h4>
              <p className="tab-description">{tabContent.profiling.description[0]}</p>
              <ul className="tab-list">
                {tabContent.profiling.description.slice(1).map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`tab-panel ${activeTab === "enable" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.enable.title}</h3>
               <h4 className="tab-subtitle1123">{tabContent.enable.steps[0]}</h4>
              <h4 className="tab-subtitle1123">{tabContent.enable.steps[1]}</h4>
              <ul className="tab-list">
                {tabContent.enable.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`tab-panel ${activeTab === "development" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.development.title}</h3>
               <h4 className="tab-subtitle1123">{tabContent.development.steps[0]}</h4>
              <h4 className="tab-subtitle1123">{tabContent.development.steps[1]}</h4>
              <ul className="tab-list">
                {tabContent.development.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`tab-panel ${activeTab === "phase" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.phase.title}</h3>
               <h4 className="tab-subtitle1123">{tabContent.phase.steps[0]}</h4>
              <h4 className="tab-subtitle1123">{tabContent.phase.steps[1]}</h4>
              <ul className="tab-list">
                {tabContent.phase.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <h3 className="cta-title">NEED A QUOTE FOR YOUR PROJECT?</h3>
          <p className="cta-description">
            Our business developers, project managers and software engineers are ready to clarify any questions you have
            related. Feel free to chat with us anytime.
          </p>
          <button className="quote-button">GET A QUOTE</button>
        </div>
        </div>
      </div>
    </div>
  )
}

export default NearProcess
